/**
 * Semantic-memory subject registry + resolution transport (PRD-0219 D-2).
 * One async fn per route under /api/v1/semantic-memory/ — every path keeps
 * its trailing slash; the backend registers slashed routes only, and a
 * slash-less call 307-redirects (dropping the auth header on some proxies).
 *
 * Error normalisation (401/403/422/503 → ApiError.authErrorKind/fieldErrors)
 * happens once in the shared client interceptor — no try/catch here.
 */

import type {
  ActivationPolicyItem,
  ActivationPolicyList,
  ActivationPolicyUpdate,
  AskProposal,
  AskProposalTransitionRequest,
  AskRequest,
  AskResponse,
  AskThread,
  AskThreadCreate,
  AskThreadDetail,
  AskThreadList,
  AskThreadUpdate,
  AttributionCoverageResponse,
  CardResponse,
  ClaimType,
  CompositionPolicy,
  CompositionPolicyUpdate,
  CompositionStatus,
  ErasureRun,
  ErasureRunList,
  ErasureRunStatus,
  EpisodeBoundaryType,
  EpisodeListResponse,
  EpisodeOutcomeStatus,
  EpisodeResponse,
  EpisodeState,
  JourneyEntryKind,
  JourneyResponse,
  RecallPackPreviewParams,
  RecallPackResponse,
  RetrievalFeedbackRequest,
  RetrievalFeedbackResponse,
  RetrievalMetricsResponse,
  SearchRequest,
  SearchResponse,
  SmemHandoff,
  SubjectAliasCreate,
  SubjectAliasListResponse,
  SubjectAliasResponse,
  SubjectCreate,
  SubjectEdgeCreate,
  SubjectEdgeListResponse,
  SubjectEdgeResponse,
  SubjectKind,
  SubjectListResponse,
  SubjectMergeRequest,
  SubjectMergeResponse,
  SubjectResponse,
  SubjectStatus,
  SubjectUpdate,
} from '@/types/smem'
import type {
  InstallationAttributionRequest,
  InstallationAttributionResponse,
} from '@/types/installationAttribution'
import { apiClient } from './client'
import { ifMatchHeaders, optionalIfMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

export const semanticMemory = {
  listSubjects: async (params: {
    kind?: SubjectKind
    status?: SubjectStatus
    q?: string
    skip?: number
    limit?: number
  }): Promise<SubjectListResponse> => {
    const response = await apiClient.get<SubjectListResponse>(
      '/api/v1/semantic-memory/subjects/',
      { params }
    )
    return response.data
  },

  createSubject: async (data: SubjectCreate): Promise<SubjectResponse> => {
    const response = await apiClient.post<SubjectResponse>(
      '/api/v1/semantic-memory/subjects/',
      data
    )
    return response.data
  },

  getSubject: async (id: string): Promise<SubjectResponse> => {
    const response = await apiClient.get<SubjectResponse>(
      `/api/v1/semantic-memory/subjects/${id}/`
    )
    return response.data
  },

  updateSubject: async (
    id: string,
    data: SubjectUpdate
  ): Promise<SubjectResponse> => {
    const response = await apiClient.patch<SubjectResponse>(
      `/api/v1/semantic-memory/subjects/${id}/`,
      data
    )
    return response.data
  },

  mergeSubject: async (
    id: string,
    data: SubjectMergeRequest
  ): Promise<SubjectMergeResponse> => {
    const response = await apiClient.post<SubjectMergeResponse>(
      `/api/v1/semantic-memory/subjects/${id}/merge/`,
      data
    )
    return response.data
  },

  listAliases: async (
    subjectId: string,
    params: { include_revoked?: boolean; skip?: number; limit?: number }
  ): Promise<SubjectAliasListResponse> => {
    const response = await apiClient.get<SubjectAliasListResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/aliases/`,
      { params }
    )
    return response.data
  },

  assertAlias: async (
    subjectId: string,
    data: SubjectAliasCreate
  ): Promise<SubjectAliasResponse> => {
    const response = await apiClient.post<SubjectAliasResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/aliases/`,
      data
    )
    return response.data
  },

  revokeAlias: async (
    subjectId: string,
    aliasId: string
  ): Promise<SubjectAliasResponse> => {
    const response = await apiClient.post<SubjectAliasResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/aliases/${aliasId}/revoke/`,
      {}
    )
    return response.data
  },

  listEdges: async (
    subjectId: string,
    params: { include_closed?: boolean; skip?: number; limit?: number }
  ): Promise<SubjectEdgeListResponse> => {
    const response = await apiClient.get<SubjectEdgeListResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/edges/`,
      { params }
    )
    return response.data
  },

  openEdge: async (
    subjectId: string,
    data: SubjectEdgeCreate
  ): Promise<SubjectEdgeResponse> => {
    const response = await apiClient.post<SubjectEdgeResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/edges/`,
      data
    )
    return response.data
  },

  closeEdge: async (
    subjectId: string,
    edgeId: string
  ): Promise<SubjectEdgeResponse> => {
    const response = await apiClient.post<SubjectEdgeResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/edges/${edgeId}/close/`,
      {}
    )
    return response.data
  },

  // Cards & Journey (PRD-0250 D-6/D-8) — versioned card serving and the
  // subject journey timeline. Both routes hang off /subjects/{id}/ like
  // aliases/edges above.

  getSubjectCard: async (
    subjectId: string,
    params?: { version?: number; as_of?: string }
  ): Promise<CardResponse> => {
    const response = await apiClient.get<CardResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/card/`,
      { params }
    )
    return response.data
  },

  // kinds is the one array-typed filter in this module — FastAPI expects
  // repeated bare keys (`kinds=a&kinds=b`), not axios's default
  // bracket-array encoding, so this route needs the shared serializer
  // (queryParams.ts) that claimReview.ts/consolidation.ts already use.
  getSubjectJourney: async (
    subjectId: string,
    params?: {
      from?: string
      to?: string
      kinds?: JourneyEntryKind[]
      skip?: number
      limit?: number
    }
  ): Promise<JourneyResponse> => {
    const response = await apiClient.get<JourneyResponse>(
      `/api/v1/semantic-memory/subjects/${subjectId}/journey/`,
      { params, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  // POST, not GET: a fleet's installation ids travel in the body, never a URL
  // or an access log, and one request answers every row a view shows
  // (attribution_installations.py's own docstring reasoning).
  attributeInstallations: async (
    data: InstallationAttributionRequest
  ): Promise<InstallationAttributionResponse> => {
    const response = await apiClient.post<InstallationAttributionResponse>(
      '/api/v1/semantic-memory/attribution/installations/',
      data
    )
    return response.data
  },

  attributionCoverage: async (
    windowDays?: number
  ): Promise<AttributionCoverageResponse> => {
    const response = await apiClient.get<AttributionCoverageResponse>(
      '/api/v1/semantic-memory/attribution/coverage/',
      { params: windowDays ? { window_days: windowDays } : undefined }
    )
    return response.data
  },

  // POST, not GET: the free-text query travels in the body so it never lands
  // in a URL or an access log (search.py's own docstring reasoning, PRD-0224
  // R-7) — the same posture attributeInstallations() carries above.
  searchDocuments: async (data: SearchRequest): Promise<SearchResponse> => {
    const response = await apiClient.post<SearchResponse>(
      '/api/v1/semantic-memory/search/documents/',
      data
    )
    return response.data
  },

  recordRetrievalFeedback: async (
    data: RetrievalFeedbackRequest
  ): Promise<RetrievalFeedbackResponse> => {
    const response = await apiClient.post<RetrievalFeedbackResponse>(
      '/api/v1/semantic-memory/retrieval-feedback/',
      data
    )
    return response.data
  },

  retrievalMetrics: async (windowDays: number): Promise<RetrievalMetricsResponse> => {
    const response = await apiClient.get<RetrievalMetricsResponse>(
      '/api/v1/semantic-memory/retrieval-metrics/',
      { params: { window_days: windowDays } }
    )
    return response.data
  },

  listEpisodes: async (params: {
    project_id?: string
    boundary_type?: EpisodeBoundaryType
    boundary_ref?: string
    outcome_status?: EpisodeOutcomeStatus
    created_from?: string
    created_to?: string
    state?: EpisodeState
    composed_by?: string
    skip?: number
    limit?: number
  }): Promise<EpisodeListResponse> => {
    const response = await apiClient.get<EpisodeListResponse>(
      '/api/v1/semantic-memory/episodes/',
      { params }
    )
    return response.data
  },

  getEpisode: async (id: string): Promise<EpisodeResponse> => {
    const response = await apiClient.get<EpisodeResponse>(
      `/api/v1/semantic-memory/episodes/${id}/`
    )
    return response.data
  },

  // preview=true always: this call is the dashboard preview arm only — the
  // real-issuance GET has no frontend caller (D-11, Q-G — cc recall pack is
  // the issuance engine).
  getRecallPackPreview: async (
    params: RecallPackPreviewParams
  ): Promise<RecallPackResponse> => {
    const { cli_system, ...query } = params
    const response = await apiClient.get<RecallPackResponse>(
      '/api/v1/semantic-memory/recall-pack/',
      {
        params: { ...query, preview: true },
        headers: { 'X-CLI-System': cli_system },
      }
    )
    return response.data
  },

  getCompositionPolicy: async (): Promise<CompositionPolicy> => {
    const response = await apiClient.get<CompositionPolicy>(
      '/api/v1/semantic-memory/composition/policy/'
    )
    return response.data
  },

  // ifMatch is the current policy_version; omit for last-write-wins semantics.
  // A mismatch returns 412 — surfaced by the 412 branch of handleMutationError.
  updateCompositionPolicy: async (
    body: CompositionPolicyUpdate,
    ifMatch?: number
  ): Promise<CompositionPolicy> => {
    const response = await apiClient.put<CompositionPolicy>(
      '/api/v1/semantic-memory/composition/policy/',
      body,
      optionalIfMatchHeaders(ifMatch)
    )
    return response.data
  },

  getCompositionStatus: async (): Promise<CompositionStatus> => {
    const response = await apiClient.get<CompositionStatus>(
      '/api/v1/semantic-memory/composition/status/'
    )
    return response.data
  },

  // Governance — activation policy (PRD-0242 D-8) and erasure run history
  // (PRD-0242 D-5). No erasure-trigger verb here by design (US-8 scopes the
  // frontend to a runs view; the erasure POST is exercised via cc/API only).

  getActivationPolicies: async (): Promise<ActivationPolicyList> => {
    const response = await apiClient.get<ActivationPolicyList>(
      '/api/v1/semantic-memory/activation-policy/'
    )
    return response.data
  },

  // ifMatch is REQUIRED (R-1/D-8) — this PUT is precondition-critical, unlike
  // the optional variant updateCompositionPolicy above.
  updateActivationPolicy: async (
    claimType: ClaimType,
    body: ActivationPolicyUpdate,
    ifMatch: number
  ): Promise<ActivationPolicyItem> => {
    const response = await apiClient.put<ActivationPolicyItem>(
      `/api/v1/semantic-memory/activation-policy/${claimType}/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  listErasureRuns: async (params: {
    status?: ErasureRunStatus
    skip?: number
    limit?: number
  }): Promise<ErasureRunList> => {
    const response = await apiClient.get<ErasureRunList>(
      '/api/v1/semantic-memory/erasure/runs/',
      { params }
    )
    return response.data
  },

  getErasureRun: async (id: string): Promise<ErasureRun> => {
    const response = await apiClient.get<ErasureRun>(
      `/api/v1/semantic-memory/erasure/runs/${id}/`
    )
    return response.data
  },

  // Ask — the bounded, cited synthesis and the caller's own conversations
  // (PRD-0271 D-4/D-6). `ask` is a billed, rate-limited POST: it is only ever
  // called from a mutation, never from a query whose cache could re-fire it.

  ask: async (body: AskRequest): Promise<AskResponse> => {
    const response = await apiClient.post<AskResponse>(
      '/api/v1/semantic-memory/ask/',
      body
    )
    return response.data
  },

  listAskThreads: async (params: {
    skip?: number
    limit?: number
  }): Promise<AskThreadList> => {
    const response = await apiClient.get<AskThreadList>(
      '/api/v1/semantic-memory/ask/threads/',
      { params }
    )
    return response.data
  },

  createAskThread: async (body: AskThreadCreate): Promise<AskThread> => {
    const response = await apiClient.post<AskThread>(
      '/api/v1/semantic-memory/ask/threads/',
      body
    )
    return response.data
  },

  // before_turn is a keyset cursor, not an offset: a conversation is appended
  // to while it is being read, so an offset would re-show or skip a turn.
  getAskThread: async (
    id: string,
    params: { before_turn?: number; limit?: number } = {}
  ): Promise<AskThreadDetail> => {
    const response = await apiClient.get<AskThreadDetail>(
      `/api/v1/semantic-memory/ask/threads/${id}/`,
      { params }
    )
    return response.data
  },

  updateAskThread: async (id: string, body: AskThreadUpdate): Promise<AskThread> => {
    const response = await apiClient.patch<AskThread>(
      `/api/v1/semantic-memory/ask/threads/${id}/`,
      body
    )
    return response.data
  },

  // Returns the archived row so the caller's cache settles on the stamp the
  // statement just wrote; archiving an already-archived thread is a 404.
  archiveAskThread: async (id: string): Promise<AskThread> => {
    const response = await apiClient.post<AskThread>(
      `/api/v1/semantic-memory/ask/threads/${id}/archive/`
    )
    return response.data
  },

  // No body: the addressee is the caller and the id is in the path. Reached
  // by the handoff_consume proposal's confirm, which posts under the
  // operator's own bearer exactly as a hand-typed consume would.
  consumeHandoff: async (handoffId: string): Promise<SmemHandoff> => {
    const response = await apiClient.post<SmemHandoff>(
      `/api/v1/semantic-memory/handoffs/${handoffId}/consume/`
    )
    return response.data
  },

  // One step of one proposal's state machine, addressed by its ordinal in the
  // run's list. 409 means the state does not admit the transition — for a
  // claim, that somebody else is already handling it.
  transitionProposal: async (
    runId: string,
    index: number,
    body: AskProposalTransitionRequest
  ): Promise<AskProposal> => {
    const response = await apiClient.patch<AskProposal>(
      `/api/v1/semantic-memory/ask/runs/${runId}/proposals/${index}/`,
      body
    )
    return response.data
  },
}
